// Filter Request Helper
// Utility for creating request data with global filters

import { getPresetRange } from '../../utils/berlinDate';

// A dateRange saved from a DateRangePresets click carries a `presetKey`
// (e.g. 'last30Days') alongside the startDate/endDate that were resolved at
// click time. Those stored dates go stale the moment the calendar day rolls
// over, so re-resolve fresh from the key here — the one place every filtered
// request (local, tab, and global) is actually built. Manual absolute ranges
// (DateRangeFilterControl, no presetKey) pass through unchanged.
const resolveDateRangeForRequest = (dateRange) => {
  if (!dateRange.presetKey) return dateRange;
  const { startDate, endDate } = getPresetRange(dateRange.presetKey, new Date());
  return { startDate, endDate, enabled: true };
};

/**
 * Create request data for analytics with global filters
 * @param {object} baseRequest - Base request object
 * @param {object} globalFilters - Global filter state
 * @returns {object} Complete request with filters
 */
export const createFilteredRequest = (baseRequest, globalFilters) => {
  const filteredRequest = {
    ...baseRequest,
    object_types: globalFilters.objectTypes?.length > 0 ? globalFilters.objectTypes : undefined,
    date_range: globalFilters.dateRange?.enabled ? resolveDateRangeForRequest(globalFilters.dateRange) : undefined,
    event_attribute_filters: (() => {
      const filtered = globalFilters.eventAttributeFilters?.filter(f => f.enabled) || [];
      if (filtered.length === 0) return undefined;
      const { matchType = 'all' } = globalFilters.eventAttributeFilterSettings || {};
      // Default match keeps the legacy list shape (backend sequential-AND path, stable cache keys)
      if (matchType === 'all') return filtered;
      return {
        enabled: true,
        match_type: matchType,
        filters: filtered.map(({ attribute, value, mode = 'include' }) => ({ attribute, value, mode })),
      };
    })(),
    object_attribute_filters: (() => {
      const filtered = globalFilters.objectAttributeFilters?.filter(f => f.enabled) || [];
      if (filtered.length === 0) return undefined;
      const { mode = 'include', matchType = 'all' } = globalFilters.objectAttributeFilterSettings || {};
      // Per-condition mode (local-filter rows, via ObjectAttributeFilterControl's showMode) is
      // independent of the group-level mode/matchType above (GlobalFilterPanel's Mode toggle) —
      // either one alone must force the config shape, since the legacy list path has no concept
      // of mode at all.
      const hasConditionExclude = filtered.some(f => (f.mode || 'include') === 'exclude');
      // Default settings + no per-condition exclude keep the legacy list shape (backend
      // include-AND path, stable cache keys)
      if (mode === 'include' && matchType === 'all' && !hasConditionExclude) return filtered;
      return {
        enabled: true,
        mode,
        match_type: matchType,
        conditions: filtered.map(({ attribute, value, mode: conditionMode }) => ({
          attribute, value, mode: conditionMode || 'include',
        })),
      };
    })(),
    activity_filters: globalFilters.activityFilters?.enabled ? globalFilters.activityFilters : undefined,
    lifecycle_activity_filters: globalFilters.lifecycleActivityFilters?.enabled ? globalFilters.lifecycleActivityFilters : undefined,
    lead_time_range: globalFilters.leadTimeRange?.enabled ? globalFilters.leadTimeRange : undefined,
    object_attribute_presence_filters: globalFilters.objectAttributePresenceFilters?.enabled ? {
      enabled: true,
      attribute_names: globalFilters.objectAttributePresenceFilters.attributeNames || [],
      mode: globalFilters.objectAttributePresenceFilters.mode || 'include',
    } : undefined,
    variant_filter: globalFilters.variantFilter?.enabled ? {
      enabled: globalFilters.variantFilter.enabled,
      object_type: globalFilters.variantFilter.objectType,
      variant_activities_list: globalFilters.variantFilter.variantActivitiesList
    } : undefined,
    edge_duration_filters: (() => {
      const filtered = (globalFilters.edgeDurationFilters || [])
        .filter((f) => f && f.edgeId && f.scopeKind && f.operator && typeof f.thresholdMs === 'number' && f.mode)
        .map((f) => ({
          edge_id: f.edgeId,
          edge_label: f.edgeLabel,
          scope_kind: f.scopeKind,
          object_type: f.objectType || undefined,
          operator: f.operator || undefined,
          threshold_ms: typeof f.thresholdMs === 'number' ? f.thresholdMs : undefined,
          mode: f.mode || undefined,
        }));
      return filtered.length > 0 ? filtered : undefined;
    })(),
    edge_containment_filters: globalFilters.edgeContainmentFilters?.enabled ? {
      enabled: globalFilters.edgeContainmentFilters.enabled,
      edges: (globalFilters.edgeContainmentFilters.edges || []).map(edge => ({
        source_activity: edge.sourceActivity,
        target_activity: edge.targetActivity,
        edge_label: edge.edgeLabel
      })),
      match_type: globalFilters.edgeContainmentFilters.matchType,
      mode: globalFilters.edgeContainmentFilters.mode
    } : undefined,
    activity_node_filters: globalFilters.activityNodeFilters?.enabled ? {
      enabled: globalFilters.activityNodeFilters.enabled,
      nodes: globalFilters.activityNodeFilters.nodes || [],
      match_type: globalFilters.activityNodeFilters.matchType,
      mode: globalFilters.activityNodeFilters.mode
    } : undefined,
  };

  return filteredRequest;
};
